import {
  type CoreServices,
  type Entity,
  HierarchyUtils,
  MetaDataComponent,
  ProductComponent,
  ReplaceUtils,
  ViewerObjectComponent,
} from '@inter-ikea-core/core'
import { IPEXViewerObject } from '@inter-ikea-core/ipex-betrakta-core-adapter'

import type { SofaPlatformCore } from '../core-api'

/**
 * TODO: This is a temporary implementation for brygga states.
 * In the future, this should be replaced with a more robust solution,
 * since this does not scale well with more features or states.
 *
 * Await design decision from the Range team
 */
const BRYGGA_STATE_MAP = {
  mirrored: ['default', 'mirrored'],
  sofabed: ['default', 'open'],
  storage: ['default', 'open'],
  reclined: ['default', 'open'],
} as const

export type BryggaState = (typeof BRYGGA_STATE_MAP)[FeatureType][number]
export type FeatureType = keyof typeof BRYGGA_STATE_MAP
// Legacy metadata shape used to differentiate brygga features
export type MetaDataShape = {
  features?: { sleep: number; storage: number; recline: boolean }
  modelid: string
}

export type ProductRefWithState = `${string}--${BryggaState}`

/**
 * Get the default brygga state for a given product id if there is one.
 */
export function getDefaultBryggaState(core: SofaPlatformCore, id: string) {
  const allProductRefs = core.coreServices.productManager.catalogs.flatMap((catalog) => catalog.allProductRefs())
  const allStates = allProductRefs.filter((prod) => prod.includes(`${id}--`))
  if (allStates.includes(`${id}--default`)) return `${id}--default`
  return id
}

/**
 * Get the next brygga state for a given entity and feature.
 */
export function getNextBryggaStateFromEntity(entity: Entity, feature: FeatureType): null | ProductRefWithState {
  const states = BRYGGA_STATE_MAP[feature]
  const productRef = entity.getComponent<ProductComponent>(ProductComponent.id)?.productRef
  if (!productRef?.includes('--')) return null

  const [productId, state] = productRef.split('--')
  const newState = state === states[0] ? states[1] : states[0]
  const newProductRef: ProductRefWithState = `${productId}--${newState}`

  return newProductRef
}

/**
 * Toggle the brygga state for all entities in the scene for a given feature.
 */
export async function toggleBryggaState(core: SofaPlatformCore, feature: FeatureType, rootEntity?: Entity) {
  const root = rootEntity ?? core.coreServices.sceneManager.scene
  const entities = HierarchyUtils.descendants(root)
    .filter((e) => e.getComponent<ProductComponent>(ProductComponent.id)?.productRef.includes('--'))
    .filter((e) => {
      /**
       * NOTE:
       * Since the current praxis is using the same state suffixes for different features,
       * we leverage the legacy metadata for planner ranges to differentiate between them.
       *
       * See top level comment for more context.
       */
      const meta = e.getComponent<MetaDataComponent>(MetaDataComponent.id)?.metaData as MetaDataShape | undefined

      if (feature === 'sofabed') {
        return meta?.features?.sleep
      }
      if (feature === 'storage') {
        return meta?.features?.storage
      }
      if (feature === 'reclined') {
        return meta?.features?.recline
      }
      return true
    })

  const didToggleState = await toggleEntitiesAsync(entities, feature, core.coreServices)

  return didToggleState
}

async function toggleEntitiesAsync(entities: Entity[], feature: FeatureType, coreServices: CoreServices) {
  let didToggleState = false

  await Promise.all(
    entities.map(async (entity) => {
      const newProductRef = getNextBryggaStateFromEntity(entity, feature)
      if (!newProductRef) return

      const newEntity = coreServices.productManager.createEntity(newProductRef)
      if (!newEntity) return

      didToggleState = true

      ReplaceUtils.fastReplaceEntity(entity, newEntity, coreServices)
      entity.dispose()

      const viewerObject = newEntity.getComponent<ViewerObjectComponent>(ViewerObjectComponent.id)?.viewerObject
      if (viewerObject instanceof IPEXViewerObject) {
        const sceneObject = viewerObject.getSceneObject()
        await sceneObject.ready
      }
    }),
  )

  return didToggleState
}
